import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '@contexts/AuthContext';
import { streamFlowApi } from '@lib/streamFlowApi';

/**
 * Phone-side pairing approval (`/pair`).
 *
 * Customer types the 6-char code their TV is showing, taps Approve.
 * The activate endpoint attaches their auth.uid() as activated_by_user_id;
 * the TV is polling status, picks up the redemption_token, exchanges it
 * for a session.
 *
 * Auth gate: ProtectedRoute already ensures the user is signed in. If
 * they followed the code from a fresh phone, Login redirects back here
 * via ?next=/pair (handled in Login.tsx).
 *
 * Convenience: accept `?code=XXXXXX` query param so a TV can also show
 * a QR code that lands the phone directly on /pair?code=... — no manual
 * typing.
 */
export default function Pair() {
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialCode = params.get('code')?.toLowerCase() ?? '';

  const [code, setCode] = useState(initialCode);
  const [submitting, setSubmitting] = useState(false);
  const [approved, setApproved] = useState<{ deviceLabel: string | null } | null>(null);

  // If we hit the page with ?code=... and the user is signed in, auto-prompt
  // (don't auto-submit — they should see what they're approving).
  useEffect(() => { /* no-op; reserved for QR auto-fill */ }, []);

  if (authLoading) {
    return <div className="flex min-h-screen items-center justify-center bg-zinc-950 text-zinc-400">…</div>;
  }
  if (!session) {
    navigate(`/login?next=${encodeURIComponent(`/pair${initialCode ? `?code=${initialCode}` : ''}`)}`, { replace: true });
    return null;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().toLowerCase();
    if (trimmed.length !== 6) {
      toast.error('Code is 6 characters');
      return;
    }
    setSubmitting(true);
    const res = await streamFlowApi.activatePairing(trimmed);
    setSubmitting(false);
    if (!res.success || !res.data) {
      toast.error(res.error?.message ?? 'Could not approve pairing');
      return;
    }
    setApproved({ deviceLabel: res.data.device_label });
    toast.success('Approved — your TV will sign in shortly');
  };

  if (approved) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
        <div className="w-full max-w-md rounded-lg border border-emerald-900 bg-emerald-950/30 p-6 text-emerald-100">
          <h2 className="mb-2 text-xl font-semibold">TV approved ✓</h2>
          <p className="text-sm opacity-90">
            {approved.deviceLabel ? <>Your TV (<span className="font-medium">{approved.deviceLabel}</span>) is signing in now.</> : <>Your TV is signing in now.</>}
            {' '}You can close this page.
          </p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 rounded-md border border-emerald-700 bg-emerald-900/40 px-3 py-1.5 text-sm hover:bg-emerald-900"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950 p-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900 p-6"
      >
        <h2 className="mb-1 text-xl font-semibold text-zinc-100">Pair a TV</h2>
        <p className="mb-4 text-sm text-zinc-400">
          Enter the 6-character code your TV is showing.
        </p>

        <input
          type="text"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
          placeholder="abcdef"
          className="mb-3 w-full rounded-md border border-zinc-700 bg-zinc-800 px-3 py-3 text-center font-mono text-2xl tracking-[0.5em] text-zinc-100 placeholder:text-zinc-600 focus:border-emerald-500 focus:outline-none"
          autoFocus
        />

        <button
          type="submit"
          disabled={submitting || code.trim().length !== 6}
          className="w-full rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? 'Approving…' : 'Approve TV'}
        </button>

        <p className="mt-4 text-xs text-zinc-500">
          The TV is polling for approval — once you tap Approve, it'll sign in within a couple of seconds.
        </p>
      </form>
    </div>
  );
}
